import type { LineItem as GQLItem } from '../../../generated/api.graphql';
import { take } from '../../../util';
import { LineItemKind } from './LineItemKind';
import * as LineItemScan from './LineItemScan';

/**
 * A single row on an order — a ticket or an add-on, told apart by `kind`.
 *
 * `balance` (the net retained after refunds) is on the wire and deliberately unmapped — an unmapped
 * field is one less thing to keep in step with the schema. Add it when something asks for it.
 */
export type LineItem = {
  id: number;
  name: string;
  price: number;
  fee: number;
  paid: number;
  kind: LineItemKind;
  scanned: boolean;
  seat?: string;
  table?: string;
  tier?: string;
  token: string;
  /** The HTML preview behind the barcode link — distinct from the `pdfUrl` download. */
  pdfPreviewUrl?: string;
  pdfUrl?: string;
  claimable: boolean;
  ticketId: number;
  /** Ticket-holder name; not necessarily the order customer. Printed on the ticket. */
  firstName?: string;
  lastName?: string;
  scans: LineItemScan.LineItemScan[];
};

export const fromGql = (item: GQLItem): LineItem => ({
  ...take<LineItem>(item, 'id', 'name', 'price', 'fee', 'paid', 'kind', 'scanned', 'seat', 'table', 'tier', 'token', 'pdfPreviewUrl', 'pdfUrl', 'claimable', 'ticketId', 'firstName', 'lastName'),
  // `scans` is non-null on the schema, but keep the default anyway: this type promises an array, and
  // a row that arrived without one would break that promise rather than merely omit a field.
  scans: (item.scans ?? []).map(LineItemScan.fromGql),
});
